/**
 * OCR engine abstraction for the SPS label splitter.
 *
 * The implementation delegates to PP-OCRv6 (PaddleOCR) via the
 * `ppu-paddle-ocr` SDK — the same engine that powers oocr.app:
 *   - browser:  `ppu-paddle-ocr/web`  + onnxruntime-web (WebGPU/WASM)
 *   - Node.js:  `ppu-paddle-ocr`      + onnxruntime-node
 *
 * This module stays platform-neutral: the concrete service is injected by
 * `createBrowserOcrEngine()` (src/core/ocrWeb.ts) or
 * `createNodeOcrEngine()` (src/core/ocrNode.ts). All field-extraction logic
 * (tracking / PO / "x OF y" indicator) lives here and is shared by both.
 */

import { LABEL_REGIONS } from './types';
import {
  extractTrackingCandidates,
  normalizePO,
  normalizeTracking,
  parseTracking,
} from './normalize';

export type OcrImage =
  | string
  | Uint8Array
  | ArrayBuffer
  | HTMLCanvasElement
  | OffscreenCanvas
  | HTMLImageElement
  | ImageData
  | Blob
  | File;

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface OcrWord {
  text: string;
  confidence: number;
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

export interface OcrRegionResult {
  text: string;
  words: OcrWord[];
  confidence: number;
}

export interface TrackingExtraction {
  value: string | null;
  corrected: boolean;
  raw: string | null;
  candidates: string[];
  confidence: number | null;
}

export interface PageFields {
  tracking: TrackingExtraction;
  po: string | null;
  poRaw: string | null;
  orderIndicator: string | null;
}

/** Minimal shape of the ppu-paddle-ocr service we wrap (duck-typed). */
export interface PaddleOcrServiceLike {
  initialize(): Promise<void>;
  recognize(
    image: unknown,
    options?: { flatten?: boolean; noCache?: boolean },
  ): Promise<PaddleOcrResultLike>;
  destroy(): Promise<void>;
}

interface PaddleRecognitionResultLike {
  text: string;
  box: { x: number; y: number; width: number; height: number };
  confidence: number;
}

interface PaddleOcrResultLike {
  text: string;
  confidence: number;
  results?: PaddleRecognitionResultLike[];
  lines?: PaddleRecognitionResultLike[][];
}

export type OcrLogger = (message: string) => void;

export interface OcrEngineOptions {
  /** Factory that constructs (but does not initialize) the OCR service. */
  createService: () => Promise<PaddleOcrServiceLike>;
  /**
   * Optional loader for string image sources.
   * Node uses fs.readFileSync; the browser falls back to fetch().
   */
  imageLoader?: (src: string) => Promise<ArrayBuffer>;
  logger?: OcrLogger;
  /** Scale factor used when rendering pages; affects same-line tolerance. */
  renderScale?: number;
}

const WORD_LINE_TOLERANCE = 12; // px at ~3x render scale

function bboxCenterY(w: OcrWord): number {
  return (w.bbox.y0 + w.bbox.y1) / 2;
}

function isTrackingLabelWord(text: string): boolean {
  const t = normalizeTracking(text);
  return (
    t.startsWith('TRACKING') ||
    t.startsWith('TRACKIN') ||
    t.startsWith('TRACKI') ||
    t.startsWith('TRAC') ||
    t.startsWith('TRAK') ||
    t.startsWith('TRK') ||
    t.startsWith('TRC')
  );
}

/**
 * Extract a tracking number from the words of an OCR pass:
 * find the "TRACKING #:" label word, then concatenate the words that follow
 * it on the same line. This is robust against line-wrap artifacts that
 * truncated naive full-line OCR on the sample labels.
 */
export function extractTrackingFromWords(words: OcrWord[]): TrackingExtraction {
  const rawWords = words ?? [];
  const labelIndex = rawWords.findIndex((w) => isTrackingLabelWord(w.text));
  const candidates: string[] = [];
  let confidence: number | null = null;

  if (labelIndex >= 0) {
    const label = rawWords[labelIndex];
    const labelCy = bboxCenterY(label);
    const rightX = label.bbox.x1;
    // Sometimes OCR merges the marker and the value into one word
    // (e.g. "TRACKING#:12Y23..."); extract from the label word itself first.
    const labelTextCandidates = extractTrackingCandidates(label.text);
    if (labelTextCandidates.length > 0) {
      const { value, corrected } = parseTracking(labelTextCandidates[0]);
      return {
        value,
        corrected,
        raw: label.text || null,
        candidates: labelTextCandidates,
        confidence: label.confidence,
      };
    }
    const valueWords = rawWords
      .slice(labelIndex + 1)
      .filter((w) => {
        const cy = bboxCenterY(w);
        return Math.abs(cy - labelCy) <= WORD_LINE_TOLERANCE && w.bbox.x0 >= rightX - 2;
      });
    const joined = valueWords.map((w) => w.text).join('');
    const { value, corrected } = parseTracking(joined);
    const all = extractTrackingCandidates(joined);
    candidates.push(...all);
    const conf = valueWords.length
      ? Math.min(...valueWords.map((w) => w.confidence))
      : null;
    return {
      value,
      corrected,
      raw: joined || null,
      candidates,
      confidence: conf,
    };
  }

  return { value: null, corrected: false, raw: null, candidates, confidence };
}

/** Fallback: extract from free-form OCR text. */
export function extractTrackingFromText(text: string): TrackingExtraction {
  const candidates = extractTrackingCandidates(text);
  const raw = text || null;
  if (candidates.length === 0) {
    return { value: null, corrected: false, raw, candidates, confidence: null };
  }
  const { value, corrected } = parseTracking(candidates[0]);
  return { value, corrected, raw, candidates, confidence: null };
}

/**
 * Extract an "x OF y" package indicator such as "1 OF 1" from OCR text.
 * Handles common OCR artifacts: "10F1", "1 OF 1", "1OF1", "2 0F 2".
 */
export function extractOrderIndicator(text: string): string | null {
  if (!text) return null;
  const normalized = text.toUpperCase().replace(/\s+/g, '');
  const m = normalized.match(/^(\d{1,3})(?:0F|OF)(\d{1,3})$/);
  if (m) return `${m[1]} OF ${m[2]}`;
  const m2 = normalized.match(/(\d{1,3})(?:0F|OF)(\d{1,3})/);
  if (m2) return `${m2[1]} OF ${m2[2]}`;
  return null;
}

/** Keep only words whose center falls inside the given rect (with a 3% margin). */
function wordsInRect(words: OcrWord[], rect: Rect): OcrWord[] {
  const marginX = Math.max(4, rect.width * 0.03);
  const marginY = Math.max(4, rect.height * 0.03);
  const x0 = rect.left - marginX;
  const y0 = rect.top - marginY;
  const x1 = rect.left + rect.width + marginX;
  const y1 = rect.top + rect.height + marginY;
  return (words ?? []).filter((w) => {
    const cx = (w.bbox.x0 + w.bbox.x1) / 2;
    const cy = (w.bbox.y0 + w.bbox.y1) / 2;
    return cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
  });
}

/** Join region words into readable lines (sorted top-to-bottom, left-to-right). */
export function joinWordsToText(words: OcrWord[]): string {
  const sorted = [...(words ?? [])].sort(
    (a, b) => bboxCenterY(a) - bboxCenterY(b) || a.bbox.x0 - b.bbox.x0,
  );
  const lines: OcrWord[][] = [];
  for (const w of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(bboxCenterY(w) - bboxCenterY(last[0])) <= WORD_LINE_TOLERANCE) {
      last.push(w);
    } else {
      lines.push([w]);
    }
  }
  return lines
    .map((line) =>
      [...line]
        .sort((a, b) => a.bbox.x0 - b.bbox.x0)
        .map((w) => w.text)
        .join(' '),
    )
    .join('\n');
}

/**
 * PP-OCRv6 engine wrapper. It recognizes the whole page in a single pass and
 * then filters the detected words by the fixed label regions, which is both
 * faster than per-region crops and avoids boundary artifacts.
 */
export class OcrEngine {
  private service: PaddleOcrServiceLike | null = null;
  private lastImage: unknown = null;
  private lastResult: OcrRegionResult | null = null;

  constructor(private options: OcrEngineOptions) {}

  private log(message: string): void {
    this.options.logger?.(message);
  }

  async init(): Promise<void> {
    if (this.service) return;
    this.log('正在加载 PP-OCRv6 引擎…');
    const service = await this.options.createService();
    await service.initialize();
    this.service = service;
    this.log('PP-OCRv6 引擎就绪');
  }

  private getService(): PaddleOcrServiceLike {
    if (!this.service) throw new Error('OcrEngine.init() must be called before recognize()');
    return this.service;
  }

  private async toInput(image: OcrImage): Promise<unknown> {
    if (typeof image === 'string') {
      if (this.options.imageLoader) return this.options.imageLoader(image);
      const res = await fetch(image);
      if (!res.ok) throw new Error(`无法加载 OCR 图片: ${image}`);
      return res.arrayBuffer();
    }
    if (image instanceof ArrayBuffer) return image;
    if (ArrayBuffer.isView(image)) {
      return image.buffer.slice(image.byteOffset, image.byteOffset + image.byteLength);
    }
    return image;
  }

  /** Full-page OCR (sparse mode) returning every detected word. */
  async recognizeFull(image: OcrImage): Promise<OcrRegionResult> {
    if (this.lastImage === image && this.lastResult) return this.lastResult;
    const input = await this.toInput(image);
    // Disable ppu-paddle-ocr's in-memory result cache: its key is a weak hash
    // of the first 1024 bytes + byte length, which collides for our uniform
    // grayscale page renders (same size, white margins) and would return the
    // first page's OCR result for every later page.
    const result = await this.getService().recognize(input, { flatten: true, noCache: true });
    const words: OcrWord[] = (result.results ?? result.lines?.flat() ?? []).map((r) => ({
      text: r.text,
      confidence: r.confidence,
      bbox: {
        x0: r.box.x,
        y0: r.box.y,
        x1: r.box.x + r.box.width,
        y1: r.box.y + r.box.height,
      },
    }));
    const out: OcrRegionResult = {
      text: result.text ?? '',
      words,
      confidence: result.confidence ?? -1,
    };
    this.lastImage = image;
    this.lastResult = out;
    return out;
  }

  /**
   * Recognize a rectangular region. Implemented over the full-page pass;
   * `psm` is accepted for API compatibility with the previous Tesseract
   * engine and is ignored by PP-OCRv6.
   */
  async recognizeRegion(
    image: OcrImage,
    rect: Rect,
    _psm?: string,
  ): Promise<OcrRegionResult> {
    const full = await this.recognizeFull(image);
    const words = wordsInRect(full.words, rect);
    const text = joinWordsToText(words);
    const confidence = words.length
      ? words.reduce((s, w) => s + w.confidence, 0) / words.length
      : -1;
    return { text, words, confidence };
  }

  /**
   * Recognize the fixed label regions on a page image and extract the fields
   * needed for grouping. `pageWidth` / `pageHeight` are the image pixel size.
   */
  async recognizePageFields(
    image: OcrImage,
    pageWidth: number,
    pageHeight: number,
  ): Promise<PageFields> {
    const r = LABEL_REGIONS;
    const rectFor = (region: { x: number; y: number; w: number; h: number }): Rect => ({
      left: Math.round(region.x * pageWidth),
      top: Math.round(region.y * pageHeight),
      width: Math.round(region.w * pageWidth),
      height: Math.round(region.h * pageHeight),
    });

    const full = await this.recognizeFull(image);

    // Tracking number: prefer the TRACKING #: line inside its region, then
    // scan the whole page, then fall back to a regex over the full text.
    let tracking = extractTrackingFromWords(wordsInRect(full.words, rectFor(r.tracking)));
    if (!tracking.value) tracking = extractTrackingFromWords(full.words);
    if (!tracking.value) tracking = extractTrackingFromText(full.text);

    const poRegionText = joinWordsToText(wordsInRect(full.words, rectFor(r.po)));
    const po = normalizePO(poRegionText) ?? normalizePO(full.text);

    let indicatorText = joinWordsToText(wordsInRect(full.words, rectFor(r.indicator)));
    let orderIndicator = extractOrderIndicator(indicatorText);
    if (!orderIndicator) {
      orderIndicator = extractOrderIndicator(
        joinWordsToText(wordsInRect(full.words, rectFor(r.banner))),
      );
    }
    if (!orderIndicator) orderIndicator = extractOrderIndicator(full.text);

    return {
      tracking,
      po,
      poRaw: poRegionText.trim() || null,
      orderIndicator,
    };
  }

  async terminate(): Promise<void> {
    if (this.service) {
      await this.service.destroy();
      this.service = null;
    }
    this.lastImage = null;
    this.lastResult = null;
  }
}
